import { useMemo } from 'react';
import { PortfolioItem } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { fillProfileUrl } from '@utils/urlUtils';

export const generateThumbnailUrl = (
  metaSiteId: string,
  w: number,
  h: number,
) => `https://www.wix.com/site-thumbnail/${metaSiteId}?w=${w}&h=${h}`;

export type ImageDimensions = { width: number; height: number };

/**
 * Returns { mediaSource, backupSource } for a single PortfolioItem.
 */
export function usePortfolioItemMediaSources(
  item: PortfolioItem | undefined,
  dimensions: ImageDimensions,
): { mediaSource: string; backupSource: string } {
  return useMemo(() => {
    if (!item) return { mediaSource: '', backupSource: '' };
    const generatedThumbnailSrc = item.metaSiteId
      ? generateThumbnailUrl(
          item.metaSiteId,
          dimensions.width,
          dimensions.height,
        )
      : '';
    const mediaSource = item.customImage
      ? fillProfileUrl(item.customImage, dimensions.width, dimensions.height)
      : generatedThumbnailSrc;
    const backupSource = item.customImage ? generatedThumbnailSrc : '';
    return { mediaSource, backupSource };
  }, [
    item?.metaSiteId,
    item?.customImage,
    dimensions.width,
    dimensions.height,
  ]);
}

export type ImageSourceItem = {
  mediaSource: string;
  backupSource?: string;
  siteName?: string;
};

/**
 * Returns an array of { mediaSource, backupSource, siteName } for each portfolio item.
 * Use when the carousel supports fallback images (e.g. SlickCarousel).
 */
export function usePortfolioItemImageSources(
  items: PortfolioItem[] | undefined,
  dimensions: ImageDimensions,
): ImageSourceItem[] {
  return useMemo(() => {
    if (!items?.length) return [];
    return items.map((item) => {
      const generatedThumbnailSrc = item.metaSiteId
        ? generateThumbnailUrl(
            item.metaSiteId,
            dimensions.width,
            dimensions.height,
          )
        : '';
      const mediaSource = item.customImage
        ? fillProfileUrl(item.customImage, dimensions.width, dimensions.height)
        : generatedThumbnailSrc;
      const backupSource = item.customImage ? generatedThumbnailSrc : undefined;
      return {
        mediaSource,
        backupSource,
        siteName: item.siteName ?? undefined,
      };
    });
  }, [items, dimensions.width, dimensions.height]);
}

/**
 * Returns an array of image URLs (mediaSource) for each portfolio item.
 */
export function usePortfolioItemImageUrls(
  items: PortfolioItem[] | undefined,
  dimensions: ImageDimensions,
): string[] {
  return useMemo(() => {
    if (!items?.length) return [];
    return items.map((item) => {
      const generatedThumbnailSrc = item.metaSiteId
        ? generateThumbnailUrl(
            item.metaSiteId,
            dimensions.width,
            dimensions.height,
          )
        : '';
      return item.customImage
        ? fillProfileUrl(item.customImage, dimensions.width, dimensions.height)
        : generatedThumbnailSrc;
    });
  }, [items, dimensions.width, dimensions.height]);
}
